import * as client from "_graphql-types";
import { filterSortPage } from "../DataProvider";
import * as types from "./types";

type Sdk = ReturnType<typeof client.getSdk>;

/*********************************************************************/
// Data Provider Functions
/*********************************************************************/

/*********************************************************************/
export async function getList(
  sdk: Sdk,
  params: types.GetListParams<client.PersonSortEnum>
) {
  const { pagination, sort, filter, meta } = params;

  const {
    personList: { items: data, total },
  } = await sdk.getListPerson({
    ...filterSortPage({
      filter,
      sort,
      nameField: client.PersonSortEnum.LastName,
      pagination,
    }),
    // Only the editable person list (which opts in via meta.requireUpdateAccess)
    // restricts to persons the user can update. Reference-input pickers and
    // dropdowns omit this so they still return the broader read-level set — see
    // issue #4855.
    ...(meta?.requireUpdateAccess
      ? { searchFilters: [{ ACCESS: { values: [client.Prud.Update] } }] }
      : {}),
  });

  return {
    data,
    total,
  };
}

/*********************************************************************/
async function getOne(
  sdk: Sdk,
  params: types.GetOneParams
): types.GetOneOut<any> {
  const id = Number(params.id);
  const { person: data } = await sdk.getOnePerson({
    id,
  });

  return { data };
}

/*********************************************************************/
async function getMany(sdk: Sdk, params: types.GetManyParams) {
  const ids = params.ids.map(id => Number(id));

  const { personMany: data } = await sdk.getManyPerson({
    ids,
  });

  return {
    data,
  };
}

/*********************************************************************/
// Ensure all diversity boolean fields default to false (not null/undefined)
function toDiversityInput(
  diversity: client.PersonDiversityInput | null | undefined
): client.PersonDiversityInput | undefined {
  if (!diversity) return undefined;
  return {
    africanAmerican: diversity.africanAmerican ?? false,
    asian: diversity.asian ?? false,
    hispanic: diversity.hispanic ?? false,
    lgbtq: diversity.lgbtq ?? false,
    other: diversity.other ?? false,
    nativeAmerican: diversity.nativeAmerican ?? false,
    veteran: diversity.veteran ?? false,
    disabled: diversity.disabled ?? false,
    nonBinary: diversity.nonBinary ?? false,
    hawaiianOrPacificIslander: diversity.hawaiianOrPacificIslander ?? false,
    woman: diversity.woman ?? false,
  };
}

/*********************************************************************/
// Shape of the person create form: PersonInput fields plus the firm picker
// and the optional "Firm Employment" section (see components/person/Create.tsx).
type PersonCreateFormData = client.PersonInput & {
  firm?: { id: string | number; name?: string };
  firmEmployment?: client.FirmEmploymentInput & {
    investmentAssignments?: Array<{
      investment?: { id: number; name?: string };
      employeeRoleEnumId?: number;
      faceOfInvestment?: boolean;
      startDate?: string;
      leftDate?: string;
    }>;
  };
};

async function create(
  sdk: Sdk,
  params: types.CreateParams<PersonCreateFormData>
) {
  const { data } = params;

  const input: client.PersonInput = {
    firstName: data.firstName,
    middleName: data.middleName,
    lastName: data.lastName,
    pronouns: data.pronouns,
    phone: data.phone,
    phone2: data.phone2,
    fax: data.fax,
    fax2: data.fax2,
    email: data.email,
    email2: data.email2,
    isCFA: data.isCFA,
    biography: data.biography,
    titleEnumId: data.titleEnumId,
    newsMonitoringStartDate: data.newsMonitoringStartDate,
    workExperienceSummary: data.workExperienceSummary,
    educationSummary: data.educationSummary,
    comments: data.comments,
    linkedin: data.linkedin,
    twitter: data.twitter,
    photo: data.photo?.rawFile,
    photoType: data.photoType,
    diversity: toDiversityInput(data.diversity),
  };

  // addPerson is nullable because it returns null on a dryRun pass; a real call
  // always returns the created record.
  const { addPerson } = await sdk.createPerson({ input });
  const personId = addPerson!.id;

  // The form's "Firm Employment" section is persisted through the existing
  // firmEmployment / investmentFirmEmployment mutations so each call runs its
  // own entitlement checks (firm update / investment update). Not atomic with
  // the person create: a failure here surfaces to the user and the already-
  // created person can be finished from their edit page.
  const firmId = data.firm?.id ? Number(data.firm.id) : undefined;
  if (firmId) {
    const employment = data.firmEmployment;

    const { addFirmEmployment } = await sdk.createFirmEmployment({
      input: {
        firmId,
        personId,
        employeeRoleEnumId: employment?.employeeRoleEnumId,
        contactTypeEnumId: employment?.contactTypeEnumId,
        auditContactTypeEnumId: employment?.auditContactTypeEnumId,
        startDate: employment?.startDate,
        isFounder: employment?.isFounder,
        isKeyPersonnel: employment?.isKeyPersonnel,
        dataContact: employment?.dataContact,
        isDataEntryContact: employment?.isDataEntryContact,
        faceOfFirm: employment?.faceOfFirm,
        focusStrategyEnumId: employment?.focusStrategyEnumId,
        isAdminContact: employment?.isAdminContact,
        authToTrade: employment?.authToTrade,
        outsideAffil: employment?.outsideAffil,
        jobDesc: employment?.jobDesc,
        comments: employment?.comments,
      },
    });

    // Rows where no investment was picked are empty iterator rows, not data.
    const assignments = (employment?.investmentAssignments ?? []).filter(
      assignment => assignment?.investment?.id
    );
    if (assignments.length) {
      await sdk.createInvestmentFirmEmploymentBulk({
        input: assignments.map(assignment => ({
          investmentId: assignment.investment!.id,
          firmEmploymentId: addFirmEmployment!.id,
          employeeRoleEnumId: assignment.employeeRoleEnumId,
          faceOfInvestment: assignment.faceOfInvestment,
          startDate: assignment.startDate || undefined,
          leftDate: assignment.leftDate || undefined,
        })),
      });
    }
  }

  return await getOne(sdk, { id: personId });
}

async function update(
  sdk: Sdk,
  params: types.UpdateParams<client.PersonInput>
) {
  const id = Number(params.id);
  const { data } = params;

  // Diversity transformation is handled by form component via transform prop
  // No need to transform here - data.diversity already has boolean fields

  const input: client.UpdatePersonMutationVariables["input"] = {
    firstName: data.firstName,
    middleName: data.middleName,
    lastName: data.lastName,
    pronouns: data.pronouns,
    phone: data.phone,
    phone2: data.phone2,
    fax: data.fax,
    fax2: data.fax2,
    email: data.email,
    email2: data.email2,
    isCFA: data.isCFA,
    biography: data.biography,
    titleEnumId: data.titleEnumId,
    newsMonitoringStartDate: data.newsMonitoringStartDate,
    workExperienceSummary: data.workExperienceSummary,
    educationSummary: data.educationSummary,
    comments: data.comments,
    linkedin: data.linkedin,
    twitter: data.twitter,
    photo: data.photo?.rawFile,
    diversity: toDiversityInput(data.diversity),
  };

  await sdk.updatePerson({ id, input: input });

  return await getOne(sdk, { id });
}

/*********************************************************************/
export const person = types.dataProvider({
  getList,
  getOne,
  getMany,
  create,
  update,
});
